/**
 * The route finder feature (M8 plan TR-3b, D-509; reach mode with the strategic service): registers the "Routes"
 * galaxy map layer (`ui/strategic/RouteFinder.tsx`, a lazy chunk so the finder stays out of the boot bundle). It
 * reads the career through the `m2` service and the Reach through `ctx.reach` on each rebuild (as the maps feature's
 * `overlayIntel`, D-503), so a career started after boot shows up. A load failure only logs; the map works without
 * the layer. No `UiActions` or `CareerPort` member.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { logger } from '../../core/logger'
import type { RouteFinderIntel, RouteFinderPanel } from '../../ui/strategic/RouteFinder'

/** Right after the maps feature (750). */
const ORDER = FEATURE_ORDER.maps + 1

/** What the panel reads of the pilot: the career from the `m2` service and the Reach, each read on demand. */
export function routeFinderIntel(ctx: Pick<GameContext, 'reach' | 'services'>): RouteFinderIntel {
  return {
    career: () => ctx.services.maybe('m2')?.career() ?? null,
    reach: () => ctx.reach,
  }
}

export interface RouteFinderFeature extends GameFeature {
  /** The installed panel (null before the chunk loads or outside the Reach). */
  readonly panel: RouteFinderPanel | null
  dispose(): void
}

export function createRouteFinderFeature(load: () => Promise<typeof import('../../ui/strategic/RouteFinder')> = () => import('../../ui/strategic/RouteFinder')): RouteFinderFeature {
  let panel: RouteFinderPanel | null = null
  let disposed = false
  return {
    name: 'routeFinder',
    order: ORDER,
    get panel(): RouteFinderPanel | null {
      return panel
    },
    init(ctx: GameContext): void {
      const client = ctx.services.maybe('strategic')
      if (client === undefined || ctx.reach === null) return
      disposed = false
      load()
        .then((m) => {
          if (disposed) return
          panel = m.installRouteFinder(client, routeFinderIntel(ctx))
        })
        .catch((err: unknown) => {
          logger.warn('Routes: the route finder failed to load', err)
        })
    },
    dispose(): void {
      disposed = true
      panel?.dispose()
      panel = null
    },
  }
}

export default createRouteFinderFeature()
